import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Crop, Download, Frame, Glasses, Maximize2, RotateCcw, RotateCw, Sparkles, Undo2, Upload, Wand2, ZoomIn, ZoomOut } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

const ZERO = {
  auto_wb: false, bw: false, rotate: 0,
  temperature: 0, tint: 0, exposure: 0, contrast: 0, saturation: 0, sharpness: 0,
};

const SLIDERS = [
  ["temperature", "lab.temperature"],
  ["tint", "lab.tint"],
  ["exposure", "lab.exposure"],
  ["contrast", "lab.contrast"],
  ["saturation", "lab.saturation"],
  ["sharpness", "lab.sharpness"],
];

const MAX_STAGING = 20;

const PRINT_SIZES = [["2x2", 2, 2], ["4x6", 4, 6], ["5x7", 5, 7],
  ["8x10", 8, 10], ["11x14", 11, 14], ["20x30", 20, 30]];

const FRAME_STYLES = {
  black: "linear-gradient(145deg,#2b2b2b,#0f0f0f)",
  wood: "linear-gradient(145deg,#a9713a,#5e3616)",
  gold: "linear-gradient(145deg,#e5c477,#9c7222)",
};

export default function AdminPhotoLab() {
  const { t, localized } = useI18n();
  const [mode, setMode] = useState("album");
  const [staging, setStaging] = useState([]);
  const fileRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [albums, setAlbums] = useState([]);
  const [albumId, setAlbumId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [active, setActive] = useState(null);
  const [edits, setEdits] = useState(ZERO);
  const [crop, setCrop] = useState(null);
  const [cropping, setCropping] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sizeKey, setSizeKey] = useState("");
  const [portrait, setPortrait] = useState(true);
  const [glareMode, setGlareMode] = useState(false);
  const [glareSize, setGlareSize] = useState(4);
  const [glareStrength, setGlareStrength] = useState("medium");
  const [spots, setSpots] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [baseW, setBaseW] = useState(0);
  const [frameOn, setFrameOn] = useState(false);
  const [frameStyle, setFrameStyle] = useState("black");
  const [matOn, setMatOn] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [uploading, setUploading] = useState(null);
  const imgRef = useRef(null);
  const scrollBoxRef = useRef(null);
  const scrollPosRef = useRef({ top: 0, left: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!eventId) return;
    setAlbums([]);
    setAlbumId("");
    api.get(`/admin/events/${eventId}/albums`).then((r) => setAlbums(r.data)).catch(() => {});
  }, [eventId]);

  const loadPhotos = useCallback(() => {
    if (!albumId) return;
    api.get(`/admin/media?album_id=${albumId}&kind=photo&page_size=400`)
      .then((r) => {
        setPhotos(r.data.media || []);
        setSelected([]);
        setActive((r.data.media || [])[0] || null);
      })
      .catch(() => {});
  }, [albumId]);

  useEffect(loadPhotos, [loadPhotos]);

  const sizeEntry = PRINT_SIZES.find(([k]) => k === sizeKey);
  const ratio = useMemo(() => {
    if (!sizeEntry) return null;
    const [, a, b] = sizeEntry;
    return portrait ? { w: a, h: b } : { w: b, h: a };
  }, [sizeEntry, portrait]);
  const sizeLabel = sizeEntry ? (portrait ? sizeKey : `${sizeEntry[2]}x${sizeEntry[1]}`) : "";
  const nameSuffix = sizeLabel ? `-${sizeLabel}` : "";

  const payloadEdits = useMemo(() => {
    const out = {};
    Object.entries(edits).forEach(([k, v]) => { if (v) out[k] = v; });
    if (crop) out.crop = crop;
    else if (ratio) out.crop_ratio = ratio;
    if (spots.length) out.glare = { strength: glareStrength, spots };
    return out;
  }, [edits, crop, ratio, spots, glareStrength]);

  const dirty = Object.keys(payloadEdits).length > 0;

  useEffect(() => {
    if (!active) { setPreview(null); return; }
    let cancelled = false;
    const id = setTimeout(() => {
      setRendering(true);
      const req = active._stage
        ? api.post("/admin/photolab/staging/preview", { staging_id: active.id, edits: payloadEdits },
          { responseType: "blob" })
        : api.post("/admin/photolab/preview", { media_id: active.id, edits: payloadEdits },
          { responseType: "blob" });
      req
        .then((r) => {
          if (cancelled) return;
          const next = URL.createObjectURL(r.data);
          setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return next; });
        })
        .catch(() => {})
        .finally(() => { if (!cancelled) setRendering(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(id); };
  }, [active, payloadEdits]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const watchJob = (jobId) => {
    const tick = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/photolab/jobs/${jobId}`);
        setJob(data);
        if (data.status === "done") {
          clearInterval(tick);
          setBusy(false);
          setJob(null);
          toast.success(`${t("lab.applied")}: ${data.done}${data.failed ? ` · ${data.failed} ✕` : ""}`);
          setEdits(ZERO);
          setCrop(null);
          loadPhotos();
        } else if (data.status === "stalled") {
          clearInterval(tick);
          setBusy(false);
          setJob(null);
          toast.error(t("lab.stalled"));
          loadPhotos();
        }
      } catch {
        clearInterval(tick);
        setBusy(false);
      }
    }, 1200);
  };

  const run = async (ids, revert = false) => {
    if (!ids.length) { toast.error(t("lab.noSelection")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(revert ? "/admin/photolab/revert" : "/admin/photolab/apply",
        { media_ids: ids, edits: revert ? {} : payloadEdits });
      setJob(data);
      watchJob(data.id);
    } catch (err) {
      setBusy(false);
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const fixOrientation = async () => {
    if (!albumId) { toast.error(t("lab.pickAlbum")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/photolab/fix-orientation", { album_id: albumId });
      setJob(data);
      watchJob(data.id);
    } catch (err) {
      setBusy(false);
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const loadStaging = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/photolab/staging");
      const withThumbs = await Promise.all(data.map(async (s) => {
        try {
          const r = await api.get(`/admin/photolab/staging/${s.id}/thumb`, { responseType: "blob" });
          return { ...s, _stage: true, thumbUrl: URL.createObjectURL(r.data) };
        } catch {
          return { ...s, _stage: true, thumbUrl: null };
        }
      }));
      setStaging(withThumbs);
      setActive((a) => (a && a._stage ? withThumbs.find((x) => x.id === a.id) || withThumbs[0] || null
        : withThumbs[0] || null));
      setSelected([]);
    } catch { /* ignored */ }
  }, []);

  useEffect(() => {
    if (mode === "pc") loadStaging();
  }, [mode, loadStaging]);

  const uploadLocal = async (files) => {
    const room = MAX_STAGING - staging.length;
    if (room <= 0) { toast.error(t("lab.pcFull")); return; }
    const picked = Array.from(files);
    const list = picked.slice(0, room);
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (let i = 0; i < list.length; i += 1) {
      const file = list[i];
      setUploading(`${i + 1}/${list.length} · ${file.name}`);
      const form = new FormData();
      form.append("files", file);
      try {
        const { data } = await api.post("/admin/photolab/staging", form, { timeout: 180000 });
        ok += data.uploaded || 0;
        (data.errors || []).forEach((e) => failed.push(e.filename || file.name));
      } catch (err) {
        const detail = err.response?.data?.detail;
        failed.push(typeof detail === "string" && detail.includes("Duplicada")
          ? `${file.name} (${t("lab.duplicate")})`
          : file.name);
        console.error("staging upload failed", file.name, err);
      }
    }
    setUploading(null);
    setBusy(false);
    if (ok) toast.success(`${t("lab.uploaded")}: ${ok}`);
    const skipped = failed.length + (picked.length - list.length);
    if (skipped) toast.error(`${t("lab.pcSkipped")}: ${skipped}${failed.length ? ` — ${failed.slice(0, 3).join(", ")}` : ""}`);
    await loadStaging();
  };

  const removeStaged = async (id) => {
    try {
      await api.delete(`/admin/photolab/staging/${id}`);
      await loadStaging();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const framePayload = frameOn ? { style: frameStyle, mat: matOn } : undefined;
  const frameSuffix = frameOn ? "-marco" : "";

  const downloadStaged = async (ids) => {
    if (!ids.length) { toast.error(t("lab.noSelection")); return; }
    setRendering(true);
    try {
      for (const id of ids) {
        const item = staging.find((s) => s.id === id);
        const { data } = await api.post("/admin/photolab/staging/preview",
          { staging_id: id, edits: payloadEdits, full: true, frame: framePayload },
          { responseType: "blob" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(data);
        a.download = (item?.filename || id).replace(/\.[^.]+$/, "") + `-editada${nameSuffix}${frameSuffix}.jpg`;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setRendering(false);
    }
  };

  const publishStaged = async (ids) => {
    if (!ids.length) { toast.error(t("lab.noSelection")); return; }
    if (!eventId) { toast.error(t("lab.pickEvent")); return; }
    if (!albumId) { toast.error(t("lab.pickAlbum")); return; }
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (let i = 0; i < ids.length; i += 1) {
      const item = staging.find((s) => s.id === ids[i]);
      setUploading(`${t("lab.publishing")} ${i + 1}/${ids.length} · ${item?.filename || ""}`);
      try {
        const { data } = await api.post("/admin/photolab/staging/publish",
          { staging_ids: [ids[i]], edits: payloadEdits, event_id: eventId, album_id: albumId },
          { timeout: 300000 });
        ok += data.published || 0;
      } catch (err) {
        failed.push(item?.filename || ids[i]);
        console.error("publish failed", err);
      }
    }
    setUploading(null);
    setBusy(false);
    if (ok) toast.success(`${t("lab.published")}: ${ok}`);
    if (failed.length) toast.error(`${t("lab.publishError")} ${failed.slice(0, 3).join(", ")}`);
    if (ok) { setEdits(ZERO); setCrop(null); }
    await loadStaging();
  };

  const download = async () => {
    if (!active) return;
    if (active._stage) { downloadStaged([active.id]); return; }
    setRendering(true);
    try {
      const { data } = await api.post("/admin/photolab/preview",
        { media_id: active.id, edits: payloadEdits, frame: framePayload }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = active.filename.replace(/\.[^.]+$/, "") + `-editada${nameSuffix}${frameSuffix}.jpg`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setRendering(false);
    }
  };

  const cropFrom = (e) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const onCropDown = (e) => {
    if (glareMode) {
      const p = cropFrom(e);
      if (p) setSpots((s) => [...s, { x: p.x, y: p.y, r: glareSize / 100 }]);
      return;
    }
    if (!cropping) return;
    dragRef.current = cropFrom(e);
    setDragging(true);
    setCrop(null);
  };
  const onCropMove = (e) => {
    if (!cropping || !dragRef.current) return;
    const p = cropFrom(e);
    const s = dragRef.current;
    if (ratio) {
      const r = imgRef.current?.getBoundingClientRect();
      if (!r) return;
      const asp = ratio.w / ratio.h;
      let wPx = Math.abs(p.x - s.x) * r.width;
      let hPx = Math.abs(p.y - s.y) * r.height;
      if (wPx / Math.max(1, hPx) > asp) hPx = wPx / asp; else wPx = hPx * asp;
      const w = Math.min(1, wPx / r.width);
      const h = Math.min(1, hPx / r.height);
      const x = Math.min(Math.max(0, p.x >= s.x ? s.x : s.x - w), 1 - w);
      const y = Math.min(Math.max(0, p.y >= s.y ? s.y : s.y - h), 1 - h);
      setCrop({ x, y, w, h });
      return;
    }
    setCrop({
      x: Math.min(s.x, p.x), y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y),
    });
  };
  const onCropUp = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    if (crop && (crop.w < 0.05 || crop.h < 0.05)) setCrop(null);
    else if (crop) setCropping(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-photolab">
      <p className="text-sm text-[#5A5752]">{t("lab.help")}</p>

      <div className="flex flex-wrap gap-2" data-testid="lab-mode">
        {[["album", "lab.modeAlbum"], ["pc", "lab.modePc"]].map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`lab-mode-${key}`}
            onClick={() => { setMode(key); setSelected([]); setActive(null); setEdits(ZERO); setCrop(null); }}
            className={`min-h-[42px] border px-5 text-xs transition-colors duration-300 ${
              mode === key ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
            }`}
          >
            {t(label)}
          </button>
        ))}
      </div>

      {mode === "pc" && (
        <div className="flex flex-wrap items-center gap-3 border border-[#C69A3B]/40 bg-white p-4">
          <GoldButton
            data-testid="lab-pc-upload"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={14} /> {t("lab.pickFiles")}
          </GoldButton>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
            multiple
            className="hidden"
            data-testid="lab-pc-input"
            onChange={(e) => { if (e.target.files?.length) uploadLocal(e.target.files); e.target.value = ""; }}
          />
          <p className="text-xs text-[#5A5752]" data-testid="lab-pc-count">
            {staging.length}/{MAX_STAGING} · {t("lab.pcHelp")}
          </p>
          {uploading && (
            <p className="text-xs text-[#8F3329]" data-testid="lab-pc-uploading">
              {t("lab.uploadingNow")} {uploading}
            </p>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <select
          className="min-h-[44px] border border-[#C69A3B]/60 bg-white px-2 text-sm"
          data-testid="lab-event-select"
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
        >
          <option value="">{t("lab.pickEvent")}</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{localized(ev, "name")}</option>
          ))}
        </select>
        <select
          className="min-h-[44px] border border-[#C69A3B]/60 bg-white px-2 text-sm"
          data-testid="lab-album-select"
          value={albumId}
          onChange={(e) => setAlbumId(e.target.value)}
          disabled={!albums.length}
        >
          <option value="">{t("lab.pickAlbum")}</option>
          {albums.map((al) => (
            <option key={al.id} value={al.id}>{localized(al, "name")}</option>
          ))}
        </select>
        <button
          type="button"
          data-testid="lab-select-all"
          onClick={() => setSelected((mode === "pc" ? staging : photos).map((p) => p.id))}
          className="min-h-[44px] border border-[#C69A3B] px-4 text-xs text-[#8F3329]"
        >
          {t("lab.selectAll")} ({(mode === "pc" ? staging : photos).length})
        </button>
        <button
          type="button"
          data-testid="lab-select-none"
          onClick={() => setSelected([])}
          className="min-h-[44px] border border-[#C69A3B] px-4 text-xs text-[#8F3329]"
        >
          {t("lab.clear")}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <div
            className="flex max-h-[62vh] justify-center overflow-auto border border-[#C69A3B]/40 bg-[#252525]"
            data-testid="lab-preview-box"
            ref={scrollBoxRef}
            onScroll={(e) => {
              scrollPosRef.current = { top: e.currentTarget.scrollTop, left: e.currentTarget.scrollLeft };
            }}
          >
            {active ? (
              <div
                className="shrink-0 self-start"
                data-testid="lab-frame-wrap"
                style={frameOn
                  ? { background: FRAME_STYLES[frameStyle], padding: "22px",
                      boxShadow: "0 24px 60px -30px rgba(0,0,0,0.9)" }
                  : undefined}
              >
                <div style={frameOn && matOn ? { background: "#FCFBF7", padding: "26px" } : undefined}>
              <div
                className="relative"
                onMouseDown={onCropDown}
                onMouseMove={onCropMove}
                onMouseUp={onCropUp}
              >
                <img
                  ref={imgRef}
                  src={preview || (active._stage ? active.thumbUrl : mediaUrl(active, "web"))}
                  alt={active.filename}
                  draggable={false}
                  data-testid="lab-preview"
                  className="block w-auto"
                  onLoad={(e) => {
                    if (zoom === 1) setBaseW(e.currentTarget.getBoundingClientRect().width);
                    const box = scrollBoxRef.current;
                    if (box) {
                      box.scrollTop = scrollPosRef.current.top;
                      box.scrollLeft = scrollPosRef.current.left;
                    }
                  }}
                  style={zoom === 1 || !baseW
                    ? { maxHeight: "62vh", maxWidth: "100%" }
                    : { width: `${Math.round(baseW * zoom)}px`, maxWidth: "none", height: "auto" }}
                />
                {crop && dragging && (
                  <div
                    className="pointer-events-none absolute border-2 border-[#C69A3B] bg-[#C69A3B]/15"
                    style={{
                      left: `${crop.x * 100}%`, top: `${crop.y * 100}%`,
                      width: `${crop.w * 100}%`, height: `${crop.h * 100}%`,
                    }}
                  />
                )}
                {glareMode && spots.map((s, i) => (
                  <span
                    key={`${s.x}-${s.y}-${i}`}
                    data-testid={`lab-glare-marker-${i}`}
                    className="pointer-events-none absolute rounded-full border-2 border-[#C69A3B]"
                    style={{
                      left: `${s.x * 100}%`,
                      top: `${s.y * 100}%`,
                      width: `${s.r * 200}%`,
                      aspectRatio: "1 / 1",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                ))}
                {rendering && (
                  <span
                    data-testid="lab-rendering"
                    className="absolute right-3 top-3 bg-[#252525]/85 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#C69A3B]"
                  >
                    {t("lab.rendering")}
                  </span>
                )}
              </div>
                </div>
              </div>
            ) : (
              <p className="p-10 text-center text-sm text-[#EFEAE0]">{t("lab.pickPhoto")}</p>
            )}
          </div>
          {active && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="lab-zoom-bar">
              <button
                type="button"
                data-testid="lab-zoom-out"
                onClick={() => setZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))))}
                className="inline-flex min-h-[38px] items-center gap-1 border border-[#C69A3B] px-3 text-xs text-[#8F3329]"
              >
                <ZoomOut size={14} /> {t("lab.zoomOut")}
              </button>
              <button
                type="button"
                data-testid="lab-zoom-in"
                onClick={() => setZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))))}
                className="inline-flex min-h-[38px] items-center gap-1 border border-[#C69A3B] px-3 text-xs text-[#8F3329]"
              >
                <ZoomIn size={14} /> {t("lab.zoomIn")}
              </button>
              <button
                type="button"
                data-testid="lab-zoom-reset"
                onClick={() => setZoom(1)}
                className="inline-flex min-h-[38px] items-center gap-1 border border-[#C69A3B] px-3 text-xs text-[#8F3329]"
              >
                <Maximize2 size={13} /> {t("lab.zoomFit")}
              </button>
              <span className="label-mono" data-testid="lab-zoom-level">{Math.round(zoom * 100)}%</span>
              {zoom > 1 && <span className="text-[11px] text-[#5A5752]">{t("lab.zoomHint")}</span>}
            </div>
          )}
          {active && (
            <p className="mt-2 text-xs text-[#5A5752]" data-testid="lab-active-name">
              {active.filename}
              {active.edits ? ` · ${t("lab.edited")}` : ""}
            </p>
          )}

          <div className="mt-5 flex max-h-[38vh] flex-wrap gap-3 overflow-y-auto" data-testid="lab-grid">
            {mode === "pc" && staging.map((s) => (
              <div key={s.id} className="w-24">
                <button
                  type="button"
                  data-testid={`lab-staged-${s.id}`}
                  onClick={() => setActive(s)}
                  className={`block h-24 w-24 overflow-hidden border-2 ${
                    active?.id === s.id ? "border-[#8F3329]" : "border-[#C69A3B]/30"
                  }`}
                >
                  {s.thumbUrl
                    ? <img src={s.thumbUrl} alt="" className="h-full w-full object-cover" />
                    : <span className="grid h-full w-full place-items-center text-[10px]">…</span>}
                </button>
                <label className="mt-1 flex items-center gap-1 text-[10px] text-[#5A5752]">
                  <input
                    type="checkbox"
                    data-testid={`lab-check-${s.id}`}
                    checked={selected.includes(s.id)}
                    onChange={() => toggle(s.id)}
                  />
                  {t("lab.select")}
                </label>
                <button
                  type="button"
                  data-testid={`lab-staged-remove-${s.id}`}
                  onClick={() => removeStaged(s.id)}
                  className="mt-1 w-full border border-[#8F3329] py-1 text-[10px] text-[#8F3329]"
                >
                  {t("btn.delete")}
                </button>
              </div>
            ))}
            {mode === "pc" && staging.length === 0 && (
              <p className="text-xs text-[#5A5752]" data-testid="lab-pc-empty">{t("lab.pcEmpty")}</p>
            )}
            {mode === "album" && photos.map((p) => {
              const on = selected.includes(p.id);
              return (
                <div key={p.id} className="w-24">
                  <button
                    type="button"
                    data-testid={`lab-photo-${p.id}`}
                    onClick={() => setActive(p)}
                    className={`block h-24 w-24 overflow-hidden border-2 ${
                      active?.id === p.id ? "border-[#8F3329]" : "border-[#C69A3B]/30"
                    }`}
                  >
                    <img
                      src={mediaUrl(p, "thumb")}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-300"
                      style={active?.id === p.id && Number(edits.rotate)
                        ? { transform: `rotate(${Number(edits.rotate)}deg) scale(0.72)` }
                        : undefined}
                    />
                  </button>
                  <label className="mt-1 flex items-center gap-1 text-[10px] text-[#5A5752]">
                    <input
                      type="checkbox"
                      data-testid={`lab-check-${p.id}`}
                      checked={on}
                      onChange={() => toggle(p.id)}
                    />
                    {t("lab.select")}
                  </label>
                </div>
              );
            })}
            {mode === "album" && photos.length === 0 && (
              <p className="text-xs text-[#5A5752]">{albumId ? t("lab.noPhotos") : t("lab.pickAlbum")}</p>
            )}
          </div>
        </div>

        <div className="space-y-4 border border-[#C69A3B]/40 bg-white p-5">
          <button
            type="button"
            data-testid="lab-auto-wb"
            onClick={() => setEdits((e) => ({ ...e, auto_wb: !e.auto_wb }))}
            className={`inline-flex min-h-[44px] w-full items-center justify-center gap-2 border text-xs transition-colors duration-300 ${
              edits.auto_wb ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
            }`}
          >
            <Wand2 size={14} /> {t("lab.autoWb")}
          </button>

          {SLIDERS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="label-mono flex justify-between">
                <span>{t(label)}</span>
                <span>{edits[key]}</span>
              </span>
              <input
                type="range"
                min="-100"
                max="100"
                step="5"
                data-testid={`lab-${key}`}
                value={edits[key]}
                onChange={(e) => setEdits((s) => ({ ...s, [key]: Number(e.target.value) }))}
                className="mt-1 w-full accent-[#8F3329]"
              />
            </label>
          ))}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="lab-rotate-left"
              onClick={() => setEdits((e) => ({ ...e, rotate: (Number(e.rotate) - 90 + 360) % 360 }))}
              className="inline-flex min-h-[40px] items-center gap-1 border border-[#C69A3B] px-3 text-xs text-[#8F3329]"
            >
              <RotateCcw size={13} /> 90°
            </button>
            <button
              type="button"
              data-testid="lab-rotate-right"
              onClick={() => setEdits((e) => ({ ...e, rotate: (Number(e.rotate) + 90) % 360 }))}
              className="inline-flex min-h-[40px] items-center gap-1 border border-[#C69A3B] px-3 text-xs text-[#8F3329]"
            >
              <RotateCw size={13} /> 90°
            </button>
            <button
              type="button"
              data-testid="lab-bw"
              onClick={() => setEdits((e) => ({ ...e, bw: !e.bw }))}
              className={`min-h-[40px] border px-3 text-xs ${
                edits.bw ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
              }`}
            >
              {t("lab.bw")}
            </button>
            <button
              type="button"
              data-testid="lab-crop-toggle"
              onClick={() => { setCropping((c) => !c); if (cropping) setCrop(null); }}
              className={`inline-flex min-h-[40px] items-center gap-1 border px-3 text-xs ${
                cropping ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
              }`}
            >
              <Crop size={13} /> {t("lab.crop")}
            </button>
          </div>
          {cropping && <p className="text-[11px] text-[#5A5752]">{t("lab.cropHelp")}</p>}
          {crop && !dragging && (
            <div className="flex flex-wrap items-center gap-3" data-testid="lab-crop-set">
              <p className="text-[11px] text-[#8F3329]">
                {t("lab.cropSet")} {Math.round(crop.w * 100)}% × {Math.round(crop.h * 100)}%
              </p>
              <button
                type="button"
                data-testid="lab-crop-clear"
                onClick={() => { setCrop(null); setCropping(false); }}
                className="min-h-[34px] border border-[#C69A3B] px-3 text-[11px] text-[#8F3329]"
              >
                {t("lab.cropClear")}
              </button>
            </div>
          )}

          <div className="space-y-2 border-t border-[#C69A3B]/30 pt-4" data-testid="lab-sizes">
            <span className="label-mono">{t("lab.sizes")}</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="lab-size-free"
                onClick={() => { setSizeKey(""); setCrop(null); }}
                className={`min-h-[36px] border px-3 text-[11px] ${
                  sizeKey === "" ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
                }`}
              >
                {t("lab.sizeFree")}
              </button>
              {PRINT_SIZES.map(([key]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`lab-size-${key}`}
                  onClick={() => { setSizeKey(key); setCrop(null); }}
                  className={`min-h-[36px] border px-3 text-[11px] ${
                    sizeKey === key ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
                  }`}
                >
                  {key}
                </button>
              ))}
            </div>
            {sizeKey && sizeKey !== "2x2" && (
              <div className="flex gap-2">
                {[[true, "lab.portrait"], [false, "lab.landscape"]].map(([val, label]) => (
                  <button
                    key={String(val)}
                    type="button"
                    data-testid={`lab-orient-${val ? "portrait" : "landscape"}`}
                    onClick={() => { setPortrait(val); setCrop(null); }}
                    className={`min-h-[36px] flex-1 border px-2 text-[11px] ${
                      portrait === val ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
                    }`}
                  >
                    {t(label)}
                  </button>
                ))}
              </div>
            )}
            {sizeKey && (
              <p className="text-[11px] text-[#5A5752]" data-testid="lab-size-help">
                {sizeLabel} · {t("lab.sizeHelp")} {t("lab.sizeBatch")}
              </p>
            )}
            <button
              type="button"
              data-testid="lab-frame-toggle"
              onClick={() => setFrameOn((f) => !f)}
              className={`mt-2 inline-flex min-h-[40px] w-full items-center justify-center gap-2 border text-xs transition-colors duration-300 ${
                frameOn ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
              }`}
            >
              <Frame size={14} /> {t("lab.frame")}
            </button>
            {frameOn && (
              <>
                <div className="flex gap-2">
                  {Object.keys(FRAME_STYLES).map((key) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`lab-frame-${key}`}
                      onClick={() => setFrameStyle(key)}
                      className={`min-h-[38px] flex-1 border px-2 text-[11px] ${
                        frameStyle === key ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
                      }`}
                    >
                      {t(`lab.frame.${key}`)}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-[11px] text-[#5A5752]">
                  <input
                    type="checkbox"
                    data-testid="lab-frame-mat"
                    checked={matOn}
                    onChange={(e) => setMatOn(e.target.checked)}
                  />
                  {t("lab.mat")}
                </label>
                <p className="text-[11px] text-[#5A5752]" data-testid="lab-frame-note">{t("lab.frameDownload")}</p>
              </>
            )}
          </div>

          <div className="space-y-2 border-t border-[#C69A3B]/30 pt-4">
            <button
              type="button"
              data-testid="lab-glare-toggle"
              onClick={() => setGlareMode((g) => !g)}
              className={`inline-flex min-h-[44px] w-full items-center justify-center gap-2 border text-xs transition-colors duration-300 ${
                glareMode ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
              }`}
            >
              <Glasses size={14} /> {t("lab.glare")}
            </button>
            {glareMode && (
              <>
                <p className="text-[11px] text-[#5A5752]">{t("lab.glareHelp")}</p>
                <label className="block">
                  <span className="label-mono flex justify-between">
                    <span>{t("lab.glareSize")}</span><span>{glareSize}</span>
                  </span>
                  <input
                    type="range" min="1" max="15" step="1"
                    data-testid="lab-glare-size"
                    value={glareSize}
                    onChange={(e) => setGlareSize(Number(e.target.value))}
                    className="mt-1 w-full accent-[#8F3329]"
                  />
                </label>
                <div className="flex gap-2">
                  {[["soft", "lab.glareSoft"], ["medium", "lab.glareMedium"], ["strong", "lab.glareStrong"]].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`lab-glare-${key}`}
                      onClick={() => setGlareStrength(key)}
                      className={`min-h-[38px] flex-1 border px-2 text-[11px] ${
                        glareStrength === key ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
                      }`}
                    >
                      {t(label)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between text-[11px] text-[#5A5752]">
                  <span data-testid="lab-glare-count">{spots.length} {t("lab.glareSpots")}</span>
                  <span className="flex gap-2">
                    <button
                      type="button"
                      data-testid="lab-glare-undo"
                      onClick={() => setSpots((s) => s.slice(0, -1))}
                      className="border border-[#C69A3B] px-2 py-1"
                    >
                      {t("lab.glareUndo")}
                    </button>
                    <button
                      type="button"
                      data-testid="lab-glare-clear"
                      onClick={() => setSpots([])}
                      className="border border-[#8F3329] px-2 py-1 text-[#8F3329]"
                    >
                      {t("lab.glareClear")}
                    </button>
                  </span>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            data-testid="lab-reset"
            onClick={() => { setEdits(ZERO); setCrop(null); setSpots([]); setSizeKey(""); }}
            className="inline-flex min-h-[40px] items-center gap-1 border border-[#C69A3B] px-3 text-xs text-[#8F3329]"
          >
            <Undo2 size={13} /> {t("lab.reset")}
          </button>

          <div className="space-y-2 border-t border-[#C69A3B]/30 pt-4">
            {mode === "pc" ? (
              <>
                <GoldButton
                  data-testid="lab-pc-publish"
                  disabled={busy || (!selected.length && !active)}
                  onClick={() => publishStaged(selected.length ? selected : [active.id])}
                  className="w-full justify-center"
                >
                  <Sparkles size={14} /> {t("lab.publish")} ({selected.length || (active ? 1 : 0)})
                </GoldButton>
                <button
                  type="button"
                  data-testid="lab-pc-download"
                  disabled={rendering || (!selected.length && !active)}
                  onClick={() => downloadStaged(selected.length ? selected : [active.id])}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 border border-[#C69A3B] text-xs text-[#8F3329] disabled:opacity-40"
                >
                  <Download size={13} /> {t("lab.download")} ({selected.length || (active ? 1 : 0)})
                </button>
                <p className="text-[11px] text-[#5A5752]">{t("lab.publishHelp")}</p>
              </>
            ) : (
              <>
                <GoldButton
                  data-testid="lab-apply-active"
                  disabled={busy || !active || !dirty}
                  onClick={() => run(active ? [active.id] : [])}
                  className="w-full justify-center"
                >
                  <Sparkles size={14} /> {t("lab.applyOne")}
                </GoldButton>
                <button
                  type="button"
                  data-testid="lab-apply-selection"
                  disabled={busy || !selected.length || !dirty || !!crop}
                  onClick={() => run(selected)}
                  className="min-h-[44px] w-full border border-[#8F3329] bg-[#8F3329] text-xs text-[#FAFAF7] disabled:opacity-40"
                >
                  {t("lab.applySelection")} ({selected.length})
                </button>
                <button
                  type="button"
                  data-testid="lab-download"
                  disabled={!active}
                  onClick={download}
                  className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 border border-[#C69A3B] text-xs text-[#8F3329] disabled:opacity-40"
                >
                  <Download size={13} /> {t("lab.download")}
                </button>
                <button
                  type="button"
                  data-testid="lab-fix-orientation"
                  disabled={busy || !albumId}
                  onClick={fixOrientation}
                  className="min-h-[40px] w-full border border-[#C69A3B] text-xs text-[#8F3329] disabled:opacity-40"
                >
                  {t("lab.fixOrientation")}
                </button>
                <p className="text-[11px] text-[#5A5752]">{t("lab.fixOrientationHelp")}</p>
                <button
                  type="button"
                  data-testid="lab-revert"
                  disabled={busy || (!selected.length && !active)}
                  onClick={() => run(selected.length ? selected : [active.id], true)}
                  className="min-h-[40px] w-full border border-[#8F3329] text-xs text-[#8F3329] disabled:opacity-40"
                >
                  {t("lab.revert")}
                </button>
              </>
            )}
            {job && (
              <p className="text-xs text-[#8F3329]" data-testid="lab-progress">
                {job.done}/{job.total}{job.failed ? ` · ${job.failed} ✕` : ""}
              </p>
            )}
          </div>
          {crop && <p className="text-[11px] text-[#5A5752]">{t("lab.cropOne")}</p>}
        </div>
      </div>
    </div>
  );
}
